import { AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { DateTime } from 'luxon';
import { getDateTime } from '../date.util';

export function requiredNotBlankValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value?.trim()) {
      return { required: true };
    }
    return null;
  };
}

export function enableRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.enabled && !control.value && control.value !== 0) {
      return { required: true };
    }
    return null;
  };
}

export function dateValidator(): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors => {
    if (control.value && !getDateTime(control.value)?.isValid) {
      return { invalidDate: true };
    }
    return null;
  };
}

export function timeValidator(): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors => {
    if (control.value && !getDateTime(control.value)?.isValid) {
      return { invalidTime: true };
    }
    return null;
  };
}

export function minDateValidator(minDate: string): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors => {
    const value = getDateTime(control.value);
    const min = getDateTime(minDate);
    if (value?.isValid && min?.isValid && value.startOf('day') < min.startOf('day')) {
      return { minDate: true };
    }
    return null;
  };
}

export function maxDateValidator(maxDate: string): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors => {
    const value = getDateTime(control.value);
    const max = getDateTime(maxDate);
    if (value?.isValid && max?.isValid && value.startOf('day') > max.startOf('day')) {
      return { maxDate: true };
    }
    return null;
  };
}

export function minTimeValidator(minDateTime: string, minDateControlName?: string): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors => {
    const min = getDateTime(minDateTime);
    if (minDateControlName) {
      const minDateControl = control?.parent?.get(minDateControlName);
      if (control && minDateControl && control.value && minDateControl.value) {
        const value = getDateTime(minDateControl.value + 'T' + control.value);
        if (value?.isValid && min?.isValid && value < min) {
          return { minTime: true };
        }
      }
      return null;
    }
    const value = getDateTime(control.value);
    if (value?.isValid && min?.isValid && value < min) {
      return { minTime: true };
    }
    return null;
  };
}

export function maxTimeValidator(maxDateTime: string, maxDateControlName?: string): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors => {
    const max = getDateTime(maxDateTime);
    if (maxDateControlName) {
      const maxDateControl = control?.parent?.get(maxDateControlName);
      if (control && maxDateControl && control.value && maxDateControl.value) {
        const value = getDateTime(maxDateControl.value + 'T' + control.value);
        if (value?.isValid && max?.isValid && value > max) {
          return { maxTime: true };
        }
      }
      return null;
    }
    const value = getDateTime(control.value);
    if (value?.isValid && max?.isValid && value > max) {
      return { maxTime: true };
    }
    return null;
  };
}

export function dateRangeValidator(startControl: string, endControl: string): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors => {
    if (control.parent && control.parent.get(startControl) && control.parent.get(endControl)) {
      const start = getDateTime(control.parent.get(startControl).value);
      const end = getDateTime(control.parent.get(endControl).value);

      if (start?.isValid && end?.isValid && start > end) {
        return { startDateGtEndDate: true };
      }
    }
    return null;
  };
}

/**
 * Time half of a date/time range split across four controls (a date control plus
 * its time control on each side). Put it on the end time control: it only fires
 * when both sides fall on the same day and the end time is earlier, so the message
 * lands on the field at fault and a multi-day range with an earlier end time stays
 * valid. Pair it with `dateRangeValidator` on the end date control and revalidate
 * both when any of the four inputs change.
 */
export function sameDayTimeRangeValidator(
  startDateControl: string,
  startTimeControl: string,
  endDateControl: string,
): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors => {
    const parent = control?.parent;
    if (!parent || !control.value) return null;

    const start = combineDateAndTime(parent.get(startDateControl)?.value, parent.get(startTimeControl)?.value);
    const end = combineDateAndTime(parent.get(endDateControl)?.value, control.value);

    if (start && end && start.hasSame(end, 'day') && start > end) {
      return { timeLt: true };
    }
    return null;
  };
}

/** Merges a date control value with a time control value into one instant. */
function combineDateAndTime(dateValue: string, timeValue: string): DateTime | null {
  if (!dateValue) return null;
  const date = DateTime.fromISO(dateValue, { zone: 'utc' });
  if (!date.isValid) return null;
  if (!timeValue) return date.startOf('day');
  const time = DateTime.fromISO(timeValue, { zone: 'utc' });
  if (!time.isValid) return date.startOf('day');
  return date.set({ hour: time.hour, minute: time.minute, second: 0, millisecond: 0 });
}

export function timeGtValidator(comparativeControlName: string): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors => {
    const comparativeControl = control?.parent?.get(comparativeControlName);
    if (control && comparativeControl) {
      const value = getDateTime(control.value);
      const comparative = getDateTime(comparativeControl.value);
      if (value?.isValid && comparative?.isValid && value > comparative) {
        return { timeGt: true };
      }
    }
    requestAnimationFrame(() => {
      if (comparativeControl?.hasError('timeLt')) {
        comparativeControl.updateValueAndValidity();
      }
    });
    return null;
  };
}

export function timeLtValidator(comparativeControlName: string): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors => {
    const comparativeControl = control?.parent?.get(comparativeControlName);
    if (control && comparativeControl) {
      const value = getDateTime(control.value);
      const comparative = getDateTime(comparativeControl.value);
      if (value?.isValid && comparative?.isValid && value < comparative) {
        return { timeLt: true };
      }
    }
    requestAnimationFrame(() => {
      if (comparativeControl?.hasError('timeGt')) {
        comparativeControl.updateValueAndValidity();
      }
    })
    return null;
  };
}

export function numberMinValidator(min: number): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined ||
      (typeof control.value === 'string' && control.value.trim() === '') ||
      control.value === '') {
      requestAnimationFrame(() => {
        control.setValue(min);
      });
      return null;
    }

    const value = Number(control.value);
    if (isNaN(value) || value < min) {
      requestAnimationFrame(() => {
        control.setValue(min);
      });
      return null;
    }

    return null;
  };
}

/**
 * Validator function to ensure two form controls have matching values.
 * Typically used for password and confirm password fields.
 *
 * @param controlName The name of the first control (e.g., 'password').
 * @param matchingControlName The name of the second control to compare against (e.g., 'confirmPassword').
 * @returns ValidatorFn that checks if the controls match and manages the 'passwordMismatch' error on the matching control.
 */
export function passwordMatchValidator(controlName: string, matchingControlName: string): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const control = formGroup.get(controlName);
    const matchingControl = formGroup.get(matchingControlName);

    if (!control || !matchingControl) {
      return null as any;
    }

    if (matchingControl?.errors?.['passwordMismatch']) {
      const errors = { ...matchingControl.errors };
      delete errors['passwordMismatch'];

      matchingControl.setErrors(Object.keys(errors).length ? errors : null);
    }

    if (!control.value || !matchingControl.value) {
      return null;
    }

    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ ...matchingControl.errors, passwordMismatch: true });
      return null;
    }

    return null;
  };
}

/**
* Validator function to check if the CEP is valid.
* @returns {ValidatorFn} Validator function for the CEP field.
*/
export function cepValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const cep = control.value;
    const cepPattern = /^[0-9]{5}-?[0-9]{3}$/;

    const valid = cepPattern.test(cep);
    return valid ? null : { invalidCep: true };
  };
}

/**
 *  Validator function to check if the CPF is valid.
  * @returns {ValidatorFn} Validator function for the CPF field.
 */
export function cpfValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.replace(/\D/g, '');
    if (!value) return null;

    if (value.length !== 11) {
      return { invalidCpfLength: true };
    }

    if (!isValidCpf(value)) {
      return { invalidCpf: true };
    }

    return null;
  };
}

/**
 * Validator function to check if the CNPJ is valid.
 * @returns {ValidatorFn} Validator function for the CNPJ field.
 */
export function cnpjValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.replace(/\D/g, '');
    if (!value) return null;

    if (value.length !== 14) {
      return { invalidCnpjLength: true };
    }

    if (!isValidCnpj(value)) {
      return { invalidCnpj: true };
    }

    return null;
  };
}

/**
 * Função auxiliar para validar CPF
 * @param cpf - O número CPF a ser validado
 * @returns Boolean indicando se o CPF é válido
 */
function isValidCpf(cpf: string): boolean {
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  const digits = cpf.split('').map(Number);
  const calculateDigit = (digits: number[], weight: number) => {
    const sum = digits.reduce((acc, digit, index) => acc + digit * (weight - index), 0);
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  const digit1 = calculateDigit(digits.slice(0, 9), 10);
  const digit2 = calculateDigit(digits.slice(0, 9).concat(digit1), 11);

  return digit1 === digits[9] && digit2 === digits[10];
}

/**
 * Função auxiliar para validar CNPJ
 * @param cnpj - O número CNPJ a ser validado
 * @returns Boolean indicando se o CNPJ é válido
 */
function isValidCnpj(cnpj: string): boolean {
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;

  const digits = cnpj.split('').map(Number);
  const calculateDigit = (digits: number[], weight: number[]) => {
    const sum = digits.reduce((acc, digit, index) => acc + digit * weight[index], 0);
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const weight1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weight2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const digit1 = calculateDigit(digits.slice(0, 12), weight1);
  const digit2 = calculateDigit(digits.slice(0, 12).concat(digit1), weight2);

  return digit1 === digits[12] && digit2 === digits[13];
}

export function maxLengthValidator(max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined) {
      return null;
    }

    const stringValue = String(value);
    const length = stringValue.length;
    if (length > max) {
      return { maxlength: { requiredLength: max, actualLength: length } };
    }
    return null;
  };
}

export function minLengthValidator(min: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined) {
      return null;
    }

    const stringValue = String(value);
    const length = stringValue.length;
    if (length < min) {
      return { minlength: { requiredLength: min, actualLength: length } };
    }
    return null;
  };
}

export function arrayMinLengthValidator(min: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!Array.isArray(control.value)) {
      return { arrayMinlength: { requiredLength: min, actualLength: 0 } };
    }

    const length = control.value.length;
    if (length < min) {
      return { arrayMinlength: { requiredLength: min, actualLength: length } };
    }
    return null;
  };
}

export function noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value && typeof value === 'string' && value.includes(' ')) {
    return { whitespace: true };
  }
  return null;
}

export function requiredField(dataControlName: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const dependentControl = control.root.get(dataControlName);
    if (dependentControl?.value && !control.value) {
      return { required: true };
    }
    return null;
  };
}

/**
 * Validator for MM/YYYY format (month/year)
 * Validates month range, year range, and format in priority order
 * Works with masked inputs M0/0000 that allow both 5/2024 and 05/2024 formats
 * Also accepts MMYYYY format (without slash) like 082025 -> 08/2025
 * 
 * Error priority: invalidMonth > invalidYear > invalidFormat
 * 
 * @param minYear - Minimum allowed year (default: 2000)
 * @param maxYearOffset - Years to add to current year for maximum (default: 10)
 * @returns ValidatorFn that validates month/year input
 */
export function monthYearValidator(minYear = 2000, maxYearOffset = 10): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined) {
      return null;
    }

    const value = control.value.toString().trim();

    if (value === '') {
      return null;
    }

    let month: number;
    let year: number;

    const monthYearWithSlashRegex = /^(\d{1,2})\/(\d{4})$/;
    const matchWithSlash = monthYearWithSlashRegex.exec(value);

    if (matchWithSlash) {
      month = parseInt(matchWithSlash[1], 10);
      year = parseInt(matchWithSlash[2], 10);
    } else {
      const monthYearSixDigitsRegex = /^(\d{2})(\d{4})$/;
      const matchSixDigits = monthYearSixDigitsRegex.exec(value);

      if (matchSixDigits) {
        month = parseInt(matchSixDigits[1], 10);
        year = parseInt(matchSixDigits[2], 10);
      } else {
        const monthYearFiveDigitsRegex = /^(\d{1})(\d{4})$/;
        const matchFiveDigits = monthYearFiveDigitsRegex.exec(value);

        if (matchFiveDigits) {
          month = parseInt(matchFiveDigits[1], 10);
          year = parseInt(matchFiveDigits[2], 10);
        } else {
          return { invalidFormat: true };
        }
      }
    }

    if (month < 1 || month > 12) {
      return { invalidMonth: true };
    }

    const currentYear = DateTime.now().year;
    if (year < minYear || year > currentYear + maxYearOffset) {
      return { invalidYear: true };
    }

    return null;
  };
}

export function thirdMeasureIsRequired(firstMeasureControlName: string, secondMeasureControlName: string, limitDiffPercentage: number): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors => {
    const firstMeasureControl = control?.parent?.get(firstMeasureControlName);
    const secondMeasureControl = control?.parent?.get(secondMeasureControlName);

    if (control && firstMeasureControl && secondMeasureControl && !control.value && firstMeasureControl.value && secondMeasureControl.value) {
      const diff = Math.abs(firstMeasureControl.value - secondMeasureControl.value);
      const limitDiff = Math.max(firstMeasureControl.value, secondMeasureControl.value) * limitDiffPercentage;
      if (diff > limitDiff) {
        return { required: true };
      }
    }
    return null;
  };
}

export function maxContentLengthValidator(max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined) {
      return null;
    }

    const div = document.createElement('div');
    div.innerHTML = value;
    const text = div.textContent || div.innerText || "";

    const length = text?.length;
    if (length > max) {
      return { maxlength: { requiredLength: max, actualLength: length } };
    }
    return null;
  };
}